/**
 * Catalogue de messages FR — **source de vérité de la forme des clés**.
 * Le type `Messages` en est dérivé ; DE/EN doivent le satisfaire à l'identique
 * (parité vérifiée par le type ET par un test — cf. `i18n.test.ts`).
 *
 * Clés organisées par domaine (`app`, `nav`, `menu`, `theme`, `lang`, `pwa`,
 * `home`, `a11y`). Pour ajouter une clé : l'ajouter ici, puis dans `.de`/`.en`
 * (le typecheck casse tant qu'une langue manque) — voir CLAUDE.md.
 */
export const fr = {
  nav: {
    home: 'Accueil',
    gallery: 'Galerie',
    folders: 'Dossiers',
    tags: 'Tags',
    aiReview: 'À valider (IA)',
    aiReviewCount_one: '{count} photo à valider (IA)',
    aiReviewCount_other: '{count} photos à valider (IA)',
  },
  breadcrumb: {
    label: "Fil d'Ariane",
    home: 'Accueil',
    exifTool: 'Nettoyage EXIF',
    exifPublishTool: 'Publier des tags EXIF',
    dedupTool: 'Dédoublonnage',
    taxonomy: 'Taxonomie',
    aiTrace: 'Journal IA',
    admin: 'Admin',
    superadmin: 'Superadmin',
    adminGeneral: 'Général',
    adminUsers: 'Comptes',
    sessionLinks: 'Liens d’accès',
    apiKeys: 'Clés d’API',
    adminGrants: 'Accès',
    adminLogs: 'Journaux',
    adminFileErrors: 'Erreurs de fichiers',
    adminMissingFiles: 'Fichiers absents',
    adminTrash: 'Poubelle',
    adminReset: 'Maintenance',
    settings: 'Paramètres',
    settingsAccount: 'Mon compte',
    settingsAppearance: 'Apparence',
    llmConfig: 'Configuration LLM',
  },
  menu: {
    openLanguage: 'Choisir la langue',
    openTheme: 'Choisir le thème',
  },
  theme: {
    light: 'Clair',
    dark: 'Sombre',
    system: 'Système',
  },
  lang: {
    fr: 'Français',
    de: 'Allemand',
    en: 'Anglais',
  },
  pwa: {
    updateAvailable: 'Nouvelle version disponible',
    reload: 'Recharger',
    reloading: 'Rechargement…',
    install: "Installer l'application",
    iosTitle: "Installer l'application",
    iosIntro: "Sur iPhone ou iPad, ajoutez {name} à votre écran d'accueil :",
    iosStep1: 'Appuyez sur le bouton Partager de Safari.',
    iosStep2: 'Choisissez « Sur l’écran d’accueil ».',
    iosClose: 'Fermer',
  },
  gallery: {
    countLabel: 'médias',
    loading: 'Chargement des médias…',
    empty: 'Aucun média',
    emptyHint: 'Lancez un scan pour indexer la bibliothèque.',
    errorTitle: 'Impossible de charger les médias',
    retry: 'Réessayer',
    retryNow: 'Réessayer maintenant',
    errorRateLimitedTitle: 'Trop de requêtes',
    errorRateLimitedRetryIn: 'Nouvel essai automatique dans {seconds} s…',
    errorRateLimitedAuto: 'Nouvel essai automatique en cours.',
    errorRateLimitedExhausted: 'Le serveur limite toujours les requêtes. Réessayez dans un moment.',
    errorNetworkTitle: 'Connexion au serveur impossible',
    errorServerTitle: 'Erreur serveur — réessayez',
    pageErrorTitle: 'Certaines photos n’ont pas pu être chargées',
    pageErrorRateLimited: 'Trop de requêtes : certaines photos n’ont pas pu être chargées.',
    pageErrorNetwork: 'Connexion perdue : certaines photos n’ont pas pu être chargées.',
    pageErrorServer: 'Erreur serveur : certaines photos n’ont pas pu être chargées.',
    pageErrorAutoRetry: 'Nouvel essai automatique en cours…',
    noLibrary: 'Aucune bibliothèque sélectionnée',
    noLibraryHint: 'Configurez VITE_LIBRARY_ID pour afficher une bibliothèque.',
    thumbnailAlt: 'Vignette de {name}',
    tabsLabel: 'Sections de la galerie',
    activeExifField: 'Filtre EXIF — champ {field}',
    activePublishInSync: 'Filtre EXIF — médias synchronisés',
    activePublishOutOfSync: 'Filtre EXIF — médias à publier',
    clearFilter: 'Effacer',
    clearExifFilter: 'Effacer le filtre EXIF',
    // Vue « À valider (IA) » : bouton PROÉMINENT ouvrant le tagueur sur tout l'ensemble filtré.
    reviewPending_one: 'Réviser cette photo',
    reviewPending_other: 'Réviser ces {count} photos',
    reviewPreparing: 'Préparation…',
    reviewEmpty: 'Aucune photo à réviser.',
    reviewError: 'Impossible de préparer la révision. Réessayez.',
  },
  folders: {
    title: 'Dossiers',
    empty: 'Aucun dossier',
    root: 'Racine',
    countLabel_one: '{count} média',
    countLabel_other: '{count} médias',
    open: 'Ouvrir le dossier {name}',
    filterLabel: 'Filtrer les dossiers',
    filterPlaceholder: 'Filtrer les dossiers…',
    sortBy: 'Trier les dossiers par',
    sortName: 'Nom',
    sortDate: 'Date',
    sortOrder: 'Ordre',
    sortAsc: 'Croissant',
    sortDesc: 'Décroissant',
    subfolders: 'Sous-dossiers',
    // XPHOTO-32 : etats d'une carte de dossier, signales PAR DU TEXTE (jamais par
    // la couleur seule). Un dossier vide n'etait tout simplement pas affichable
    // avant que l'arbre des dossiers existe.
    emptyBadge: 'Vide',
    subfoldersOnly: 'Sous-dossiers seulement',
    // Le dossier n'apparait que parce que des photos le referencent : le dernier scan
    // ne l'a pas retenu (repertoire exclu du listage, ou index en retard).
    notIndexed: 'Absent de l’index',
    photosHere: 'Photos',
    breadcrumbLabel: 'Chemin du dossier',
  },
  tagsTab: {
    title: 'Tags',
    empty: 'Aucun tag pour le moment',
    emptyHint: 'Les tags apparaîtront après le tagging.',
    select: 'Filtrer sur le tag {label}',
    countLabel_one: '{count} média',
    countLabel_other: '{count} médias',
  },
  sort: {
    label: 'Trier & filtrer',
    open: 'Trier & filtrer',
    sortBy: 'Trier par',
    random: 'Aléatoire',
    takenAt: 'Date de prise de vue',
    filename: 'Nom de fichier',
    sizeBytes: 'Taille',
    createdAt: 'Date d’ajout',
    viewCount: 'Vues',
    rating: 'Note',
    order: 'Ordre',
    asc: 'Croissant',
    desc: 'Décroissant',
    reshuffle: 'Re-mélanger',
    reshuffling: 'Mélange en cours…',
    reshuffleError: 'Le re-mélange a échoué. Réessayez.',
    type: 'Type',
    typeAll: 'Tous',
    typeImage: 'Images',
    typeAnimatedImage: 'Images animées',
    typeVideo: 'Vidéos',
    typeVideoWithSound: 'Vidéos avec son',
    filterSection: 'Filtres',
    untagged: 'Non taguées',
    aiReview: 'À valider (IA)',
  },
  density: {
    open: 'Choisir la densité',
    S: 'Compacte',
    M: 'Standard',
    L: 'Confortable',
  },
  tools: {
    open: 'Ouvrir le menu Outils',
    openOverflow: 'Plus',
    upload: 'Importer des fichiers',
    scan: 'Lancer un scan',
    scanning: 'Scan en cours…',
    scanFailed: 'Échec du scan',
    scanAlreadyRunning: 'Une analyse est déjà en cours ou en attente. Attendez qu’elle se termine.',
    tagAiConfirm:
      "Proposer des tags par l'IA pour toute la bibliothèque ? Les propositions seront à valider ensuite.",
    tagAiConfirmSelection:
      "Proposer des tags par l'IA pour les {count} médias sélectionnés ? Les propositions seront à valider ensuite.",
    tagAiConfirmTitle: "Tagger avec l'IA",
    tagAiConfirmAction: 'Lancer le tagging',
    tagAiCancel: 'Annuler',
    tagAiQueuing: 'Lancement…',
    tagAiNoProvider:
      'Aucun fournisseur LLM configuré. Un superadmin peut en configurer un dans Superadmin → Configuration LLM.',
    tagAiDisabled: "Les fonctions IA sont désactivées par l'administrateur.",
    tagAiFailed: 'Échec du lancement du tagging IA',
    dedup: 'Dédoublonnage',
    exif: 'Nettoyage EXIF',
    exifPublish: 'Publier des tags vers EXIF',
    fixExtensions: 'Corriger les extensions',
    cleanEmptyFolders: 'Nettoyer les dossiers vides',
    taxonomy: 'Gérer les tags',
  },
  select: {
    toggle: 'Sélectionner {name}',
    // Bascule du MODE sélection (barre d'outils) : entrer / sortir. Sortir vide la sélection.
    enterMode: 'Sélectionner',
    exitMode: 'Terminé',
    selectedCount: '{count} sélectionnés',
    // Déclencheur du menu regroupant les actions de sélection (Déplacer/Noter/…/Supprimer).
    actions: 'Actions…',
    clear: 'Effacer la sélection',
    selectAll: 'Tout sélectionner ({count})',
    deselectAll: 'Tout désélectionner',
    selecting: 'Sélection…',
    selectAllError: 'Impossible de tout sélectionner. Réessayez.',
    selectAllForbidden: 'Vous n’avez pas les droits pour sélectionner ces médias.',
    download: 'Télécharger la sélection',
    downloading: 'Préparation de l’archive…',
    downloadError: 'Impossible de télécharger la sélection. Réessayez.',
    downloadForbidden: 'Vous n’avez pas les droits pour télécharger ces médias.',
    tagSelection: 'Réviser / valider les tags',
    slideshow: 'Diaporama',
  },
  badge: {
    missing: 'Absent du disque',
    video: 'Vidéo',
  },
  viewer: {
    title: 'Visionneuse',
    open: 'Ouvrir {name} dans la visionneuse',
    close: 'Fermer la visionneuse',
    previous: 'Média précédent',
    next: 'Média suivant',
    download: 'Télécharger l’original',
    counter: '{index} / {total}',
    showInfo: 'Afficher le nom du fichier',
    hideInfo: 'Masquer le nom du fichier',
    showDetail: 'Afficher le détail',
    hideDetail: 'Masquer le détail',
    loading: 'Chargement du média…',
    error: 'Impossible de charger le média',
    errorNotFound: 'Ce média n’existe plus',
    errorNotFoundHint: 'Il a peut-être été supprimé ou déplacé.',
    errorForbidden: 'Accès refusé à ce média',
    errorForbiddenHint: 'Il ne fait pas partie de ce qui vous a été partagé.',
    errorNetwork: 'Connexion au serveur impossible',
    errorNetworkHint: 'Vérifiez votre connexion, puis rechargez la page.',
    errorUnknownHint: 'Rechargez la page ou réessayez plus tard.',
    retry: 'Réessayer',
    play: 'Démarrer le diaporama',
    pause: 'Mettre le diaporama en pause',
    loopOn: 'Activer la lecture en boucle',
    loopOff: 'Désactiver la lecture en boucle',
    speed: 'Vitesse du diaporama',
    speedValue: '{seconds} s',
    cap: "Durée maximale d'un média long",
    capValue: 'max {seconds} s',
    capUnlimited: 'sans limite',
    zoomIn: 'Agrandir',
    zoomOut: 'Réduire',
    zoomReset: 'Taille par défaut',
    zoomLevel: 'Zoom : {percent} %',
    videoUnsupported: 'Votre navigateur ne peut pas lire cette vidéo.',
    openRating: 'Noter la photo',
    closeRating: 'Fermer la note',
    openComments: 'Commenter la photo',
    closeComments: 'Fermer les commentaires',
    // Actions d'ÉCRITURE de la barre du haut (les rotations réutilisent `rotate.cw/ccw`).
    trash: 'Mettre cette photo à la corbeille',
    // Bandeau de confirmation annulable, affiché juste après la mise à la corbeille.
    trashed: '{name} à la corbeille',
    undo: 'Annuler',
    // Menu de débordement mobile regroupant les quatre bascules d'affichage, dont les
    // entrées nomment CE QUI s'affiche (l'état est porté par la case cochée).
    displayOptions: 'Options d’affichage',
    optionRating: 'Note',
    optionComments: 'Commentaires',
    optionCaption: 'Nom du fichier',
    optionDetail: 'Détail',
  },
  publicShare: {
    count_one: '{count} média',
    count_other: '{count} médias',
    loading: 'Chargement du partage…',
    empty: 'Ce partage ne contient aucun média.',
    openMedia: 'Ouvrir le média {n}',
    viewerLabel: 'Média {n} sur {total}',
    position: '{n} / {total}',
    previous: 'Média précédent',
    next: 'Média suivant',
    download: 'Télécharger le fichier d’origine',
    close: 'Fermer',
    goneTitle: 'Ce lien n’est plus valable',
    goneBody:
      'Le partage a peut-être expiré ou été retiré par son auteur. Demandez-lui un nouveau lien.',
    errorTitle: 'Partage indisponible',
    errorBody: 'Le partage n’a pas pu être chargé. Vérifiez votre connexion.',
    retry: 'Réessayer',
  },
  share: {
    create: {
      title: 'Créer un lien public',
      intro: 'Ce lien sera consultable sans compte, par toute personne qui le reçoit.',
      titleLabel: 'Titre du lien',
      scopeLabel: 'Contenu',
      scopeRoot: 'Toute la bibliothèque que vous pouvez voir',
      scopeFolder: 'Dossier {folder} et ses sous-dossiers',
      scopeSelection: '{count} média(s) sélectionné(s)',
      expiryLabel: 'Expire',
      expiry: { d7: '7 jours', d30: '30 jours', y1: '1 an', never: 'Jamais' },
      lightweight: 'Partager des copies allégées',
      lightweightHint:
        'WebP, 2560 px au plus, métadonnées retirées. Les photos plus petites gardent leur taille.',
      warnOriginals:
        'Les fichiers d’origine seront envoyés tels quels, avec leurs métadonnées — dont le lieu de prise de vue. Les vidéos sont toujours envoyées en fichier d’origine.',
      warnVideosOnly:
        'Les photos seront envoyées en copies sans métadonnées. Les vidéos sont toujours envoyées en fichier d’origine.',
      cancel: 'Annuler',
      submit: 'Créer le lien',
      submitting: 'Création…',
      forbidden: 'Ce contenu dépasse ce que vous avez le droit de voir.',
      failed: 'Le lien n’a pas pu être créé.',
      doneTitle: 'Lien créé',
      doneIntro: 'Copiez-le maintenant et envoyez-le à qui de droit.',
      onceOnly: 'Ce lien n’est affiché qu’une seule fois : il ne pourra pas être retrouvé ensuite.',
      copy: 'Copier',
      done: 'Terminé',
    },
    list: {
      loading: 'Chargement des partages…',
      empty: 'Aucun lien public pour l’instant.',
      failed: 'Les partages n’ont pas pu être chargés.',
      retry: 'Réessayer',
      mediaCount: '{count} médias',
      viewCount: '{count} vues',
      noExpiry: 'sans échéance',
      expiresOn: 'expire le {date}',
      revoke: 'Révoquer',
      status: { live: 'Actif', expired: 'Expiré', revoked: 'Révoqué' },
    },
    mineTitle: 'Mes partages',
    mineIntro: 'Les liens publics que vous avez créés.',
    adminTitle: 'Partages',
    adminIntro: 'Tous les liens publics de la bibliothèque, quel que soit leur auteur.',
    short: 'Partager',
    photo: 'Partager cette photo',
    view: 'Partager cette vue',
    copied: 'Lien copié',
    linkLabel: 'Lien à copier',
    close: 'Fermer',
  },
  rating: {
    label: 'Note',
    star: 'Noter {n}/5',
    clear: 'Retirer la note',
    error: 'Impossible d’enregistrer la note',
    errorReadonly: 'Accès en lecture seule : note non modifiable',
  },
  comments: {
    bodyLabel: 'Commentaire',
    bodyPlaceholder: 'Écrire un commentaire…',
    add: 'Ajouter',
    save: 'Enregistrer',
    saving: 'Enregistrement…',
    cancel: 'Annuler',
    edit: 'Modifier le commentaire',
    delete: 'Supprimer le commentaire',
    confirmTitle: 'Supprimer le commentaire',
    confirmDelete: 'Supprimer ce commentaire ?',
    confirmAction: 'Supprimer',
    deleting: 'Suppression…',
    edited: 'modifié',
    empty: 'Aucun commentaire',
    error: 'Impossible d’enregistrer le commentaire',
    errorReadonly: 'Accès en lecture seule : commentaire non modifiable',
  },
  info: {
    title: 'Informations',
    path: 'Chemin',
    mime: 'Type MIME',
    dimensions: 'Dimensions',
    takenAt: 'Prise de vue',
    duration: 'Durée',
    exif: 'Métadonnées EXIF',
    gps: 'GPS lat/long',
    gpsOpen: 'Ouvrir dans Google Maps',
    noExif: 'Aucune donnée EXIF',
    divergence: 'Divergence disque ↔ base',
    missing: 'Fichier absent du disque',
    present: 'Présent sur le disque',
    seenAt: 'Vu le',
  },
  exif: {
    title: 'Nettoyage EXIF',
    close: 'Fermer le nettoyage EXIF',
    intro:
      'Repérez les métadonnées présentes, parcourez les photos qui portent un champ, puis effacez un champ précis (ou un effacement complet) en lot sur toute la bibliothèque ou un dossier. La base de données reste la source de vérité.',
    loading: 'Analyse de la bibliothèque…',
    errorTitle: "Impossible de charger l'analyse EXIF",
    retry: 'Réessayer',
    analysisTitle: 'Analyse de présence',
    analysisTotal: '{total} médias présents analysés',
    analysisHint:
      'Nombre de médias portant chaque champ. « Voir » filtre la galerie sur ce champ ; « Effacer ce champ » supprime ce champ en lot sur la portée choisie.',
    groupCapture: 'Prise de vue',
    groupCamera: 'Appareil',
    groupRights: 'Droits',
    groupKeywords: 'Mots-clés',
    fieldPresence: '{present} / {total} présents',
    filterByField: 'Filtrer la galerie sur le champ {field}',
    viewField: 'Voir',
    eraseField: 'Effacer ce champ',
    eraseFieldAction: 'Effacer le champ {field} sur les médias de la portée',
    cleanedBadge: '✓ nettoyé',
    // Options d'effacement partagées.
    eraseOptionsTitle: "Options d'effacement",
    eraseScopeAll: 'toute la bibliothèque',
    eraseScopeFolder: 'le dossier {folder}',
    // Publication (déplacée dans l'outil « Publier des tags vers EXIF »).
    publishTitle: 'Publication des tags',
    publishHint:
      'Exporte les tags validés en base (IPTC:Keywords + XMP dc:subject) vers les fichiers divergents uniquement.',
    taggedAssets: 'Médias tagués',
    inSync: 'Synchronisés',
    outOfSync: 'À publier',
    filterInSync: 'Filtrer la galerie sur les médias synchronisés',
    filterOutOfSync: 'Filtrer la galerie sur les médias à publier',
    publish: 'Publier',
    previewPublish: 'Aperçu de publication',
    publishToolTitle: 'Publier des tags vers EXIF',
    publishToolClose: "Fermer l'outil de publication",
    publishToolIntro:
      'Exporte les tags validés en base vers les métadonnées des fichiers (IPTC:Keywords + XMP dc:subject). Seuls les fichiers divergents sont réécrits.',
    eraseTitle: 'Effacement complet (avancé)',
    eraseHint:
      'Efface TOUTES les métadonnées embarquées (GPS compris). Action par lot, irréversible sans sauvegarde. Pour conserver un champ, utilisez « Effacer ce champ » ci-dessus.',
    eraseAllOptionsNote:
      'Les options ci-dessus (simulation, sauvegarde _original, portée dossier) s’appliquent AUSSI à « Effacer tout » — la simulation ne modifie rien.',
    erase: 'Effacer tout',
    folderScope: 'Portée (dossier)',
    folderAll: 'Toute la bibliothèque',
    keepOriginal: 'Conserver un original (_original)',
    keepOriginalHint: 'Sauvegarde le fichier d’origine à côté avant écriture.',
    noBackupWarning:
      'Sans sauvegarde : les fichiers seront modifiés directement, sans copie de secours (_original). Action irréversible.',
    dryRun: 'Simulation (dry-run)',
    dryRunHint: 'Simule sans modifier les fichiers.',
    queuing: 'Enfilement…',
    jobQueued: 'Job enfilé (#{id})',
    jobFailed: "Échec de l'enfilement du job",
    errorWriteDisabled:
      'Écriture désactivée sur la bibliothèque : impossible d’effacer. Remontez /library en écriture côté serveur.',
    // Modale de confirmation d'effacement (ciblé ou global).
    eraseConfirmClose: 'Fermer',
    eraseConfirmWarning: 'Cette action est irréversible.',
    eraseConfirmCancel: 'Annuler',
    eraseConfirmApply: 'Effacer',
    eraseConfirmApplying: 'Enfilement…',
    eraseConfirmFieldTitle: 'Effacer ce champ EXIF ?',
    eraseConfirmFieldBody:
      'Le champ {field} sera effacé des métadonnées de {scope}. Les autres champs sont préservés.',
    eraseConfirmAllTitle: 'Effacer toutes les métadonnées ?',
    eraseConfirmAllBody:
      'Toutes les métadonnées de {scope} seront effacées (hors groupes conservés). Le GPS est toujours effacé.',
    // Auto-rafraîchissement de l'analyse après un effacement (pas de rescan complet).
    autoResyncNote:
      'L’effacement met à jour automatiquement les photos concernées dans l’index — pas besoin de rescanner toute la bibliothèque. L’analyse se rafraîchit à la fin du job.',
    refreshAnalysis: 'Actualiser l’analyse',
    // Nettoyage des sauvegardes _original (sûreté).
    originals: {
      title: 'Fichiers de sauvegarde _original',
      hint: 'ExifTool crée un fichier « …_original » à chaque écriture si l’option de sauvegarde est activée. Recherchez-les pour savoir combien vous en avez, puis supprimez-les.',
      scan: 'Rechercher les sauvegardes',
      found: '{count} sauvegardes trouvées (taille totale {size}).',
      none: 'Aucune sauvegarde _original trouvée.',
      error: 'Impossible de rechercher les sauvegardes _original.',
      delete: 'Supprimer les sauvegardes',
      confirmTitle: 'Supprimer les sauvegardes _original ?',
      confirmBody:
        '{count} fichiers de sauvegarde _original de {scope} ({size}) seront supprimés définitivement. Les fichiers d’origine ne sont pas touchés.',
      confirmApply: 'Supprimer',
    },
  },
  dedup: {
    title: 'Dédoublonnage',
    close: "Fermer l'outil de dédoublonnage",
    // Onglets (copies identiques / images similaires).
    tabsLabel: 'Type de doublons',
    tabExact: 'Copies identiques',
    tabSimilar: 'Images similaires',
    similarTitle: 'Images similaires',
    intro:
      'Regroupe les quasi-doublons (ré-encodages, redimensionnements, retouches légères) par proximité perceptuelle. Le regroupement peut se tromper : par défaut chaque image est CONSERVÉE ; effacez uniquement les vrais doublons.',
    loading: 'Chargement des groupes…',
    // Résumé (en-tête).
    summaryClusters_one: '{count} groupe',
    summaryClusters_other: '{count} groupes',
    summaryDuplicates_one: '{count} doublon candidat',
    summaryDuplicates_other: '{count} doublons candidats',
    lastRunAt: 'Dernière analyse : {when}',
    neverRun: 'Jamais analysé',
    // Analyse.
    thresholdLabel: 'Seuil de similarité',
    thresholdHint: 'Distance de Hamming maximale (0 = identique, plus élevé = plus permissif).',
    runAnalysis: 'Lancer une analyse',
    running: 'Enfilement…',
    analysisQueued: 'Analyse enfilée. Suivez la progression dans la cloche, puis actualisez.',
    refresh: 'Actualiser les groupes',
    // Membres d'un groupe.
    resolution: '{width} × {height}',
    resolutionUnknown: 'Résolution inconnue',
    keeperSuggested: 'Original suggéré',
    openInViewer: 'Ouvrir {name} dans la visionneuse',
    // Décision par image (Conserver / Effacer).
    decisionLabel: 'Décision pour {name}',
    decisionKeep: 'Conserver',
    decisionDelete: 'Effacer',
    lastKeptHint: 'Impossible d’effacer la dernière image conservée du groupe.',
    // Application.
    applyButton: 'Supprimer les doublons sélectionnés',
    applyNone: 'Aucun doublon sélectionné',
    selectedForDeletion_one: '{count} fichier à supprimer',
    selectedForDeletion_other: '{count} fichiers à supprimer',
    // Modale de confirmation.
    confirmTitle: 'Supprimer définitivement les doublons ?',
    confirmClose: 'Fermer',
    confirmWarning: 'Cette action est irréversible.',
    confirmBody:
      '{count} fichiers seront SUPPRIMÉS du disque. Les originaux choisis sont conservés. Les vignettes et l’index seront mis à jour au prochain scan.',
    confirmCancel: 'Annuler',
    confirmApply_one: 'Supprimer {count} fichier',
    confirmApply_other: 'Supprimer {count} fichiers',
    applying: 'Enfilement…',
    applyRunning: 'Suppression en cours…',
    applyQueued: 'Suppression enfilée. Suivez la progression dans la cloche.',
    // Écarter un groupe « faux positif » (ce ne sont pas des doublons).
    notDuplicates: 'Ce ne sont pas des doublons',
    dismissing: 'Retrait…',
    dismissError: 'Échec du retrait du groupe.',
    dismissForbidden: 'Accès en écriture requis pour écarter un groupe.',
    // Erreurs.
    errorTitle: 'Impossible de charger les groupes de doublons.',
    retry: 'Réessayer',
    errorWriteDisabled:
      'Écriture désactivée sur la bibliothèque : impossible de supprimer. Remontez /library en écriture côté serveur.',
    errorApply: "Échec de l'enfilement de la suppression.",
    errorRun: "Échec de l'enfilement de l'analyse.",
    // Vide.
    emptyNeverRun: 'Aucune analyse encore lancée. Lancez une analyse pour détecter les doublons.',
    emptyNoDuplicates: 'Aucun doublon détecté. Votre bibliothèque est propre.',
    // Pagination.
    prevPage: 'Page précédente',
    nextPage: 'Page suivante',
    pageStatus: 'Groupes {from}–{to} sur {total}',
    // --- Copies EXACTES (section distincte, en haut de l'écran) ---
    exact: {
      title: 'Copies exactes',
      intro:
        'Fichiers strictement identiques (mêmes pixels) présents à plusieurs endroits sur le disque. L’inventaire provient du scan. Choisissez le chemin à garder par image ; les autres copies seront supprimées.',
      refresh: 'Actualiser',
      loading: 'Chargement des copies exactes…',
      // Résumé.
      summaryGroups_one: '{count} groupe de copies exactes',
      summaryGroups_other: '{count} groupes de copies exactes',
      summaryRedundant_one: '{count} copie redondante',
      summaryRedundant_other: '{count} copies redondantes',
      inventoryAt: 'Inventaire à jour au {when}',
      neverScanned: 'Jamais scanné',
      // Groupe.
      resolution: '{width} × {height}',
      resolutionUnknown: 'Résolution inconnue',
      kindImage: 'Image',
      kindVideo: 'Vidéo',
      noThumbnail: 'Aperçu indisponible',
      openInViewer: 'Ouvrir {name} dans la visionneuse',
      canonical: 'Indexé',
      identicalBytes: 'octets identiques',
      reencoded: 'ré-encodage (pixels identiques)',
      // Choix du chemin à garder.
      chooseKeepLabel: 'Choisir le chemin à conserver',
      willKeep: 'Sera conservé',
      willDelete: 'Sera supprimé',
      // Raison du choix par défaut du keeper (icône + texte, jamais la couleur seule).
      keptLightest: 'le plus léger',
      keptCanonical: 'copie indexée',
      // Application.
      applyButton: 'Supprimer les copies redondantes',
      applyNone: 'Aucune copie à supprimer',
      selectedForDeletion_one: '{count} copie à supprimer',
      selectedForDeletion_other: '{count} copies à supprimer',
      pageScopeNote:
        'La suppression porte sur les groupes de la page affichée. Traitez chaque page avant de changer de page.',
      // Modale de confirmation.
      confirmTitle: 'Supprimer définitivement les copies redondantes ?',
      confirmClose: 'Fermer',
      confirmWarning: 'Cette action est irréversible.',
      confirmBody:
        '{count} copies redondantes seront SUPPRIMÉES du disque. Un exemplaire est conservé par image. L’index sera mis à jour au prochain scan.',
      confirmCancel: 'Annuler',
      confirmApply_one: 'Supprimer {count} copie',
      confirmApply_other: 'Supprimer {count} copies',
      applying: 'Enfilement…',
      applyRunning: 'Suppression en cours…',
      applyQueued: 'Suppression enfilée. Suivez la progression dans la cloche.',
      // Erreurs.
      errorTitle: 'Impossible de charger les copies exactes.',
      retry: 'Réessayer',
      errorWriteDisabled:
        'Écriture désactivée sur la bibliothèque : impossible de supprimer. Remontez /library en écriture côté serveur.',
      errorApply: "Échec de l'enfilement de la suppression.",
      // Vide.
      emptyNeverScanned: 'Aucun scan effectué — lancez un scan pour détecter les copies exactes.',
      emptyNoCopies: 'Aucune copie exacte trouvée.',
      // Pagination.
      prevPage: 'Page précédente',
      nextPage: 'Page suivante',
      pageStatus: 'Groupes {from}–{to} sur {total}',
    },
  },
  passwordField: {
    show: 'Afficher le mot de passe',
    hide: 'Masquer le mot de passe',
  },
  auth: {
    title: 'Connexion',
    emailPlaceholder: 'vous@exemple.com',
    identifierLabel: 'Identifiant ou email',
    passwordLabel: 'Mot de passe',
    stayLoggedIn: 'Rester connecté',
    submit: 'Se connecter',
    submitting: 'Connexion…',
    invalidCredentials: 'Identifiants incorrects.',
    genericError: 'Connexion impossible. Réessayez.',
    checkingSession: 'Vérification de la session…',
    networkErrorTitle: 'Connexion au serveur impossible.',
    retry: 'Réessayer',
    tooManyAccounts: 'Trop de comptes connectés simultanément.',
    cancelAddAccount: 'Annuler',
    account: 'Compte',
    signOut: 'Se déconnecter',
    roleSuperadmin: 'Superadmin',
    roleAdmin: 'Administrateur',
    roleUser: 'Utilisateur',
  },
  accountSwitcher: {
    title: 'Comptes connectés',
    addAccount: 'Ajouter un compte',
    active: 'Compte actif',
    logoutAccount: 'Déconnecter ce compte',
    confirmLogoutAll: 'Confirmer : tout déconnecter ?',
    theme: 'Thème',
    language: 'Langue',
    myAccount: 'Mon compte',
  },
  settings: {
    title: 'Paramètres',
    close: 'Fermer',
    intro: 'Consultez votre compte et changez votre mot de passe.',
    tabAccount: 'Compte',
    tabAppearance: 'Apparence',
    defaultSortLabel: 'Ordre de tri par défaut de l’accueil',
    defaultSortHint:
      'Ordre appliqué à l’accueil ; ailleurs (Dossiers, Tags), c’est le dernier tri choisi qui est conservé.',
    accountTitle: 'Compte',
    emailLabel: 'Adresse e-mail',
    nameLabel: 'Nom affiché',
    roleLabel: 'Rôle',
    changePasswordTitle: 'Changer le mot de passe',
    currentPasswordLabel: 'Mot de passe actuel',
    passwordViaLink:
      'Vous \u00eates entr\u00e9 par un lien d\u2019acc\u00e8s partag\u00e9 : ce compte n\u2019a pas de mot de passe personnel \u00e0 changer. Demandez un nouveau lien \u00e0 l\u2019administrateur si n\u00e9cessaire.',
    newPasswordLabel: 'Nouveau mot de passe',
    confirmPasswordLabel: 'Confirmer le nouveau mot de passe',
    submit: 'Changer le mot de passe',
    submitting: 'Modification…',
    success: 'Mot de passe changé. Vos autres sessions ont été déconnectées.',
    errorCurrentWrong: 'Mot de passe actuel incorrect.',
    errorMismatch: 'Les deux nouveaux mots de passe ne correspondent pas.',
    errorTooShort: 'Le nouveau mot de passe doit contenir au moins 8 caractères.',
    genericError: 'Une erreur est survenue. Réessayez.',
    apiKeys: {
      title: 'Clés d’API',
      intro:
        'Créez des clés pour accéder à votre bibliothèque en lecture seule depuis un outil externe (API v1).',
      labelLabel: 'Libellé de la clé',
      labelPlaceholder: 'Ex. Mon script d’export',
      create: 'Créer une clé',
      creating: 'Création…',
      secretWarning: 'Copiez cette clé maintenant : elle ne sera plus affichée.',
      copy: 'Copier',
      copied: 'Copié',
      loading: 'Chargement…',
      empty: 'Aucune clé d’API.',
      neverUsed: 'Jamais utilisée',
      lastUsed: 'Dernière utilisation : {date}',
      revoked: 'Révoquée',
      revoke: 'Révoquer',
      confirmRevoke: 'Confirmer la révocation',
      delete: 'Supprimer',
      deleteConfirm: 'Confirmer la suppression',
      cancel: 'Annuler',
      errorLabel: 'Le libellé est requis.',
      errorGeneric: 'Impossible de créer la clé. Réessayez.',
      errorLoad: 'Impossible de charger les clés.',
      docLink: 'Voir la documentation de l’API (OpenAPI)',
    },
  },
  superadmin: {
    open: 'Superadmin',
    title: 'Superadmin',
  },
  sessionLinks: {
    tab: 'Liens d’accès',
    title: 'Liens d’accès',
    intro:
      'Un lien ouvre une session sans mot de passe pour un compte existant (lecteur ou contributeur). Ne partagez jamais l’URL par un canal public.',
    createTitle: 'Créer un lien d’accès',
    targetLabel: 'Compte cible',
    chooseTarget: 'Choisir un compte…',
    labelLabel: 'Libellé',
    labelPlaceholder: 'ex. « Famille » ou « Club photo »',
    create: 'Créer le lien',
    creating: 'Création…',
    writeWarning:
      'Attention : ce lien donne l’accès en ÉCRITURE (import, suppression) à quiconque le possède.',
    urlWarning: 'Copiez cette URL maintenant : elle ne sera plus jamais affichée.',
    copy: 'Copier l’URL',
    copied: 'Copié',
    errorNoTarget: 'Choisissez un compte cible.',
    errorLabel: 'Indiquez un libellé.',
    errorTarget:
      'Ce compte ne peut pas recevoir de lien d’accès (compte superadmin ou hors bibliothèque).',
    errorGeneric: 'La création a échoué. Réessayez.',
    errorLoad: 'Chargement des liens impossible.',
    retry: 'Réessayer',
    loading: 'Chargement…',
    empty: 'Aucun lien d’accès.',
    created: 'Créé le {date}',
    lastUsed: 'dernière utilisation {date}',
    neverUsed: 'jamais utilisé',
    active: 'Actif',
    revoked: 'Révoqué',
    revoke: 'Révoquer',
    revoking: 'Révocation…',
    confirmRevokeTitle: 'Révoquer ce lien ?',
    confirmRevokeBody:
      'Le lien « {label} » cessera immédiatement de fonctionner, et les personnes entrées par ce lien seront déconnectées. Les autres liens vers le même compte ne sont pas touchés. Cette action est irréversible.',
    cancel: 'Annuler',
  },
  admin: {
    open: 'Admin',
    title: 'Administration',
    close: 'Fermer',
    tabGeneral: 'Général',
    tabUsers: 'Comptes',
    tabGrants: 'Accès',
    loading: 'Chargement…',
    errorTitle: 'Chargement impossible.',
    retry: 'Réessayer',
    // Section Général (nom du site, LOT 3).
    general: {
      title: 'Général',
      intro:
        'Nom du site affiché dans l’onglet du navigateur, le pied de page et l’installation de l’application.',
      appNameLabel: 'Nom du site',
      save: 'Enregistrer',
      saveAppNameAria: 'Enregistrer le nom du site',
      saveGatewayAria: 'Enregistrer la passerelle d’authentification',
      saving: 'Enregistrement…',
      saved: 'Nom du site enregistré.',
      error: 'Enregistrement impossible. Réessayez.',
      conflict:
        'Ces réglages ont été modifiés ailleurs entre-temps. Rechargez la page avant d’enregistrer, pour ne pas écraser la modification de l’autre éditeur.',
      // Agrandissement des petites photos (visionneuse).
      upscaleTitle: 'Agrandissement des petites photos',
      upscaleIntro:
        'Sur grand écran, une photo plus petite que la fenêtre est agrandie dans la visionneuse jusqu’à ce pourcentage (100 % = taille d’origine, sans agrandissement).',
      upscaleLabel: 'Agrandissement maximal (%)',
      upscaleHelp: 'Entre {min} et {max} %. Valeur par défaut : 150 %.',
      saveUpscaleAria: 'Enregistrer l’agrandissement des petites photos',
      upscaleSaved: 'Agrandissement enregistré.',
      upscaleInvalid: 'Valeur invalide. Attendu : un nombre entier entre {min} et {max}.',
      // Passerelle d'authentification (ADR-0008).
      gatewayTitle: 'Passerelle d’authentification',
      gatewayIntro:
        'Permet aux membres d’une autre application (un forum, par exemple) d’ouvrir les liens de photos partagés entre eux sans avoir à se connecter ici. Laissez le champ vide pour désactiver : les visiteurs sans session verront l’écran de connexion habituel.',
      gatewayLabel: 'URL de la passerelle, dans l’autre application',
      gatewayPlaceholder: 'https://forum.exemple.ch/photos',
      gatewayHowTitle: 'Ce que cette adresse doit faire, dans l’autre application :',
      gatewayStep1:
        'N’être accessible qu’aux membres connectés à cette autre application : c’est elle qui sait qui est le visiteur, pas nous.',
      gatewayStep2:
        'Lire le paramètre « next » que nous lui transmettons : c’est la page demandée ici (par exemple /dossiers?dossier=Trombis).',
      gatewayStep3:
        'Renvoyer le visiteur vers cette page, en ajoutant à l’adresse le jeton d’un lien d’accès (?access=…) gardé dans sa configuration. Le jeton ne circule ainsi jamais dans les liens que les membres se partagent.',
      gatewayWarning:
        'Sécurité : l’autre application doit refuser tout « next » qui n’est pas un chemin commençant par une seule barre oblique — sinon le jeton peut être détourné vers un site tiers.',
      gatewaySaved: 'Passerelle enregistrée.',
      gatewayDisabled: 'Passerelle désactivée.',
      gatewayInvalid: 'Adresse invalide. Attendu : une URL commençant par http:// ou https://.',
    },
    // Section Comptes.
    usersTitle: 'Comptes utilisateurs',
    usersHint: 'Créez, modifiez le rôle ou supprimez des comptes.',
    usersEmpty: 'Aucun compte.',
    colEmail: 'E-mail',
    colName: 'Nom',
    colRole: 'Rôle',
    colActions: 'Actions',
    createUserTitle: 'Créer un compte',
    fieldEmail: 'Adresse e-mail',
    fieldName: 'Nom affiché',
    fieldRole: 'Rôle',
    fieldPassword: 'Mot de passe',
    grantFullRead: 'Accès en lecture à toute la bibliothèque',
    grantFullReadHint:
      'Décochez pour un lecteur restreint à un sous-dossier (à définir ensuite dans « Accès »).',
    adminWriteHint: 'Un administrateur a accès en écriture à toute la bibliothèque.',
    create: 'Créer',
    creating: 'Création…',
    userCreated: 'Compte créé.',
    editRole: 'Modifier le rôle de {name}',
    delete: 'Supprimer',
    deleting: 'Suppression…',
    confirmDeleteTitle: 'Supprimer le compte',
    confirmDelete: 'Supprimer le compte {name} ? Cette action est irréversible.',
    cancel: 'Annuler',
    // Réinitialisation du mot de passe par le superadmin (champ révélable inline).
    resetPassword: 'Réinitialiser le mot de passe',
    resetPasswordFor: 'Nouveau mot de passe pour {name}',
    resetPasswordPlaceholder: 'Nouveau mot de passe (min. 8)',
    resetPasswordSubmit: 'Valider',
    resettingPassword: 'Réinitialisation…',
    resetPasswordDone: 'Mot de passe réinitialisé. Les sessions de ce compte ont été déconnectées.',
    resetPasswordError: 'La réinitialisation a échoué. Réessayez.',
    conflictUser: 'Ce compte est en conflit avec un compte existant.',
    conflictEmail: 'Cet e-mail est déjà utilisé.',
    conflictLastAdmin: 'Impossible : c’est le dernier superadmin.',
    cannotDeleteSelf: 'Impossible de supprimer votre propre compte.',
    deleteSelfDisabled: 'Votre compte',
    genericError: 'Une erreur est survenue. Réessayez.',
    // Section Accès (grants).
    grantsTitle: 'Accès à la bibliothèque',
    grantsHint: 'Accordez un accès en LECTURE SEULE à un sous-arbre de la bibliothèque.',
    grantsReadOnly:
      'Ces accès sont en lecture seule : le bénéficiaire ne peut ni modifier ni supprimer.',
    grantsEmpty: 'Aucun accès accordé.',
    noLibrarySelected: 'Aucune bibliothèque associée à votre compte.',
    colBeneficiary: 'Bénéficiaire',
    colSubtree: 'Sous-arbre',
    subtreeRoot: 'Racine (toute la bibliothèque)',
    createGrantTitle: 'Accorder un accès',
    fieldBeneficiary: 'Bénéficiaire',
    fieldUserId: 'Identifiant utilisateur',
    chooseBeneficiary: 'Choisir un bénéficiaire…',
    fieldSubtree: 'Sous-arbre (préfixe de chemin)',
    subtreePlaceholder: 'ex. 2024/vacances (vide = racine)',
    grant: 'Accorder',
    granting: 'Attribution…',
    grantCreated: 'Accès accordé.',
    revoke: 'Révoquer',
    revoking: 'Révocation…',
    confirmRevokeTitle: 'Révoquer l’accès',
    confirmRevoke: 'Révoquer l’accès de {name} ?',
    conflictGrant: 'Cet accès existe déjà.',
    notFoundUser: 'Utilisateur introuvable.',
    // Contrôle de purge unifié (réutilisable — erreurs de fichiers ET requêtes).
    purge: {
      dateLabel: 'Jusqu’à cette date (incluse)',
      button: 'Purger jusqu’à cette date',
      title: 'Purger les entrées',
      confirm: 'Purger toutes les entrées jusqu’au {date} inclus ? Cette action est irréversible.',
      confirmButton: 'Purger',
      cancel: 'Annuler',
      purging: 'Purge…',
      purged: '{count} entrée(s) supprimée(s).',
      invalidDate: 'Date invalide.',
      error: 'La purge a échoué. Réessayez.',
    },
    // Section Journaux (superadmin) — journalisation de toutes les requêtes.
    // --- Journal d'AUDIT (audit B1) : actions métier + leur auteur ---
    audit: {
      tab: 'Audit',
      title: "Journal d'audit",
      intro:
        'Qui a fait quoi : connexions, changements de mot de passe et traitements (déplacement, effacement EXIF, purge, compression…), avec leur auteur.',
      retention:
        'Conservation automatique : 24 mois. Ce journal ne peut pas être effacé depuis cette page.',
      empty: 'Aucune entrée dans le journal.',
      colTimestamp: 'Horodatage',
      colAction: 'Action',
      colUser: 'Auteur',
      colSummary: 'Résumé',
      colDetails: 'Détails',
      noUser: 'Non attribué',
      dryRun: 'Simulation (aucune modification)',
      showDetails: 'Voir les détails',
      detailsUnreadable: 'Détails illisibles',
      previous: 'Précédent',
      next: 'Suivant',
      range: '{from}–{to} sur {total}',
      filterAction: 'Action',
      filterUser: 'Auteur',
      filterFrom: 'Du',
      filterTo: 'Au',
      actionAll: 'Toutes les actions',
      userAll: 'Tous les auteurs',
    },
    // --- Sessions actives d'un compte (audit B2) ---
    sessions: {
      button: 'Sessions',
      count: '{count} session(s) active(s)',
      empty: 'Aucune session active.',
      loadError: 'Chargement des sessions impossible.',
      unknownAgent: 'Appareil inconnu',
      unknownLink: 'lien supprimé',
      lastUsed: 'Dernière activité : {when}',
      expires: 'Expire le {when}',
      viaLink: 'Entré par le lien « {label} »',
      current: 'Votre session actuelle',
      revoke: 'Révoquer',
      revoking: 'Révocation…',
      revokeError: 'La révocation a échoué. Réessayez.',
    },
    logs: {
      tab: 'Journaux',
      title: 'Journaux des requêtes',
      intro:
        'Toutes les requêtes API sont journalisées (méthode, chemin, statut, durée, utilisateur).',
      retention: 'Conservation automatique : 3 mois.',
      empty: 'Aucune entrée dans le journal.',
      colTimestamp: 'Horodatage',
      colMethod: 'Méthode',
      colPath: 'Chemin',
      colStatus: 'Statut',
      colUser: 'Utilisateur',
      colDuration: 'Durée',
      colError: 'Erreur',
      noUser: '—',
      duration: '{ms} ms',
      statusSuccess: 'Succès',
      statusRedirect: 'Redirection',
      statusClientError: 'Erreur client',
      statusServerError: 'Erreur serveur',
      previous: 'Précédent',
      next: 'Suivant',
      range: '{from}–{to} sur {total}',
      // Barre de filtres (Utilisateur / Statut = classe HTTP / Méthode = verbe).
      filterUser: 'Utilisateur',
      filterStatus: 'Statut',
      filterMethod: 'Méthode',
      userAll: 'Tous les utilisateurs',
      statusAll: 'Tous les statuts',
      status2xx: '2xx (succès)',
      status3xx: '3xx (redirection)',
      status4xx: '4xx (erreur client)',
      status5xx: '5xx (erreur serveur)',
      methodAll: 'Toutes les méthodes',
      methodGet: 'GET',
      methodPost: 'POST',
      methodPatch: 'PATCH',
      methodDelete: 'DELETE',
    },
    // Section Erreurs de fichiers (Lot C) — journal des échecs de traitement.
    fileErrors: {
      tab: 'Erreurs de fichiers',
      title: 'Erreurs de traitement de fichiers',
      intro:
        'Échecs et anomalies rencontrés par les traitements (scan, déplacement, EXIF, dédoublonnage) : fichier illisible, format invalide (ex. PDF nommé .png), vignette en échec…',
      empty: 'Aucune erreur enregistrée.',
      noValue: '—',
      previous: 'Précédent',
      next: 'Suivant',
      range: '{from}–{to} sur {total}',
      // Colonnes.
      colTimestamp: 'Horodatage',
      colSeverity: 'Statut',
      colMethod: 'Méthode',
      colUser: 'Utilisateur',
      colPath: 'Chemin relatif',
      colMime: 'MIME détecté',
      colMessage: 'Message',
      // Barre de filtres.
      filterUser: 'Utilisateur',
      filterStatus: 'Statut',
      filterMethod: 'Méthode',
      userAll: 'Tous les utilisateurs',
      statusAll: 'Tous les statuts',
      statusError: 'Erreurs',
      statusSkipped: 'Ignorés',
      statusWarning: 'Avertissements',
      methodAll: 'Toutes les méthodes',
      methodScan: 'Scan',
      methodMove: 'Déplacement',
      methodExif: 'EXIF',
      methodDedup: 'Dédoublonnage',
      methodTrash: 'Purge de la corbeille',
      // Libellés de type de job dans la table.
      jobScan: 'Scan',
      jobThumbnail: 'Vignette',
      jobAiTag: 'Tag IA',
      jobDedup: 'Dédoublonnage',
      jobDedupApply: 'Suppression de doublons',
      jobDedupExactApply: 'Suppression de copies exactes',
      jobExifPublish: 'Publication EXIF',
      jobExifErase: 'Effacement EXIF',
      jobMove: 'Déplacement',
      jobCompress: 'Compression',
      jobRotate: 'Rotation',
      jobOriginalsCleanup: 'Nettoyage _original',
      jobTrashPurge: 'Purge de la corbeille',
      jobRenameExtensions: 'Correction des extensions',
      jobEmptyFoldersCleanup: 'Nettoyage des dossiers vides',
      // Encart de réconciliation disque ↔ index (dernier scan).
      reconTitle: 'Réconciliation du dernier scan',
      reconSummary:
        '{walked} fichiers sur le disque = {indexed} indexés (en galerie) + {collapsed} copies exactes fusionnées + {skipped} ignorés.',
      reconWhen: 'Dernier scan terminé le {when}.',
      reconWhenDry: 'Dernier aperçu (dry-run) le {when}.',
      reconWalked: 'Fichiers sur le disque',
      reconIndexed: 'Médias indexés (en galerie)',
      reconCollapsed: 'Copies exactes fusionnées',
      reconSkipped: 'Fichiers ignorés',
      reconByReason: 'Ignorés, par raison',
      reconUnaccounted: 'Écart non expliqué : {count} (relancez un scan pour actualiser).',
      reconReasonUnsupported: 'Extension non prise en charge',
      reconReasonNonMedia: 'Fichier non média (annexe / système)',
      reconReasonUnreadable: 'Fichier illisible',
      reconReasonDecode: 'Décodage / traitement en échec',
      reconReasonMismatched: 'Type incohérent (ex. PDF nommé .png)',
      // Ventilation des extensions refusées + exports TXT.
      reconExtensionsTitle: 'Extensions non prises en charge, par occurrence',
      reconExtensionNone: '(sans extension)',
      reconDownloadSkipped: 'Télécharger la liste des fichiers ignorés (.txt)',
      reconDownloadNonMedia: 'Télécharger les fichiers non média (.txt)',
    },
    // Section Maintenance (superadmin) — réinitialisation « repartir de zéro ».
    reset: {
      tab: 'Maintenance',
      title: 'Réinitialiser la bibliothèque',
      intro:
        'Repartez de zéro pour retester l’entrée en galerie et la génération des vignettes. Un nouveau scan reconstruira tout.',
      deletes: 'Sera supprimé : l’index des médias (base de données) et les vignettes (cache).',
      preserves:
        'Sera préservé : vos fichiers ORIGINAUX et leurs métadonnées EXIF (jamais touchés), les comptes, la bibliothèque et les accès.',
      button: 'Tout réinitialiser',
      // Modale de confirmation lourde.
      modalTitle: 'Réinitialiser la bibliothèque ?',
      modalWarning:
        'Cette action est IRRÉVERSIBLE. Elle vide l’index des médias et supprime toutes les vignettes.',
      modalSafe:
        'Aucun fichier original n’est modifié ni supprimé, et aucune métadonnée EXIF sur disque n’est touchée : la réinitialisation ne concerne que la base de données et le cache de vignettes.',
      confirmLabel: 'Pour confirmer, saisissez {word} ci-dessous.',
      confirmWord: 'REINITIALISER',
      confirmPlaceholder: 'Tapez le mot de confirmation',
      confirm: 'Réinitialiser définitivement',
      resetting: 'Réinitialisation…',
      cancel: 'Annuler',
      close: 'Fermer',
      done: 'Réinitialisation terminée : {assets} média(s) et {thumbnails} vignette(s) supprimé(s). Vous pouvez relancer un scan.',
      error: 'La réinitialisation a échoué. Réessayez.',
    },
    // Section Poubelle (Lot 2) — gestion de la corbeille (soft-delete + purge définitive).
    // Section « Fichiers absents » (audit A5) — médias dont le fichier a disparu du disque.
    missingFiles: {
      tab: 'Fichiers absents',
      title: 'Fichiers absents',
      intro:
        'Médias indexés dont le fichier a DISPARU du disque. Ils sont masqués de toute la galerie : c’est le seul endroit d’où vous pouvez encore les traiter.',
      hint: 'Mettez-les à la corbeille, puis purgez-la (onglet « Poubelle ») pour supprimer définitivement la ligne et ses vignettes. Aucun fichier n’est touché : il n’y en a plus.',
      empty: 'Aucun fichier absent. Tout l’index correspond au disque.',
      error: 'L’opération a échoué. Réessayez.',
      errorForbidden: 'Opération refusée : accès en lecture seule.',
      trashSelected: 'Mettre à la corbeille ({count})',
      trashed: '{count} média(s) mis à la corbeille.',
      refresh: 'Actualiser',
      selectAll: 'Tout sélectionner sur cette page',
      selectOne: 'Sélectionner {name}',
      colPath: 'Chemin',
      colSize: 'Taille',
      colSeenAt: 'Vu pour la dernière fois',
      seenUnknown: 'Inconnu',
      range: '{from}–{to} sur {total}',
      previous: 'Précédent',
      next: 'Suivant',
    },
    trash: {
      tab: 'Poubelle',
      title: 'Poubelle',
      intro:
        'Médias mis à la corbeille : restaurez-les ou purgez-les DÉFINITIVEMENT (suppression du fichier sur le disque, irréversible).',
      empty: 'La corbeille est vide.',
      error: 'L’opération a échoué. Réessayez.',
      errorWriteDisabled:
        'Purge impossible : la bibliothèque est montée en lecture seule (écriture désactivée).',
      restoreSelected: 'Restaurer la sélection ({count})',
      restored: '{count} média(s) restauré(s).',
      purgeSelected: 'Effacement définitif ({count})',
      purgeAll: 'Vider la poubelle',
      purgeQueued: 'Purge lancée — suivez son avancement dans les notifications.',
      purging: 'Purge…',
      cancel: 'Annuler',
      confirmTitle: 'Purge définitive ?',
      confirmButton: 'Purger définitivement',
      confirmWarning:
        'Cette action est IRRÉVERSIBLE : les fichiers sont SUPPRIMÉS du disque et retirés de la base.',
      confirmAll: '{count} média(s) de la corbeille seront définitivement supprimés.',
      confirmSelection: '{count} média(s) sélectionné(s) seront définitivement supprimés.',
      deletedWhen: 'Supprimé le {when}',
      range: '{from}–{to} sur {total}',
      previous: 'Précédent',
      next: 'Suivant',
    },
  },
  bugReport: {
    open: 'Signaler un bug',
    title: 'Signaler un bug',
    close: 'Fermer',
    intro:
      "Copiez ce rapport (ou téléchargez-le en .json) et envoyez-le comme vous voulez. Rien n'est transmis automatiquement.",
    descriptionLabel: 'Description (à remplir par vous)',
    descriptionPlaceholder: "Décrivez ce qui s'est passé et ce que vous attendiez…",
    technicalLabel: 'Contexte technique (lecture seule)',
    noPasswords: 'Ce rapport ne contient pas vos mots de passe.',
    copy: 'Copier',
    copied: 'Copié !',
    download: 'Télécharger .json',
    crashTitle: "L'application a rencontré une erreur",
    crashHint: 'Vous pouvez recharger la page ou signaler le problème.',
    crashHintInline:
      'Cet écran a rencontré une erreur. Vous pouvez naviguer ailleurs, recharger ou signaler le problème.',
    reload: 'Recharger',
  },
  notifications: {
    open: 'Notifications ({count} en cours)',
    title: 'Activité',
    empty: 'Aucune activité',
    loading: 'Chargement de l’activité…',
    error: 'Impossible de charger l’activité',
    clearAll: 'Tout effacer',
    dismiss: 'Supprimer',
    cancel: 'Annuler',
    progress: '{done}/{total}',
    cancelForbidden: 'Accès en lecture seule : annulation impossible',
    cancelFailed: 'L’annulation a échoué. Réessayez.',
    retry: 'Relancer',
    retrying: 'Relance…',
    retryQueued: 'Relancé : le traitement repart.',
    retryForbidden: 'Accès en lecture seule : relance impossible',
    retryAlreadyRunning: 'Un traitement du même type est déjà en cours.',
    retryNotRetryable: 'Ce type de traitement ne peut pas être relancé automatiquement.',
    retryFailed: 'La relance a échoué. Réessayez.',
    eta: 'Fin estimée dans {eta}',
    etaSeconds: '≈ {value} s',
    etaMinutes: '≈ {value} min',
    startedAt: 'Démarré à {time}',
    ranRange: '{start} → {end}',
    typeScan: 'Scan',
    typeThumbnail: 'Génération de vignettes',
    typeAiTag: 'Tagging IA',
    typeDedup: 'Dédoublonnage',
    typeDedupApply: 'Suppression des doublons',
    typeDedupExactApply: 'Suppression des copies exactes',
    typeExifPublish: 'Publication EXIF',
    typeExifErase: 'Effacement EXIF',
    typeMove: 'Déplacement de fichiers',
    typeCompress: 'Compression sans perte',
    typeRotate: 'Rotation',
    typeOriginalsCleanup: 'Nettoyage des sauvegardes _original',
    typeTrashPurge: 'Purge de la corbeille',
    typeRenameExtensions: 'Correction des extensions',
    typeEmptyFoldersCleanup: 'Nettoyage des dossiers vides',
    statusQueued: 'En attente',
    statusRunning: 'En cours',
    statusDone: 'Terminé',
    statusFailed: 'Échoué',
    statusCanceled: 'Annulé',
    doneNoResult: 'Terminé sans résultat',
    doneNoResultHint: 'Toutes les opérations ont échoué. Voir Admin › Erreurs de fichiers.',
    reviewAiTags: 'Réviser les tags IA',
    refreshGallery: 'Actualiser la galerie',
    // Résumés du résultat d'un job terminé (cloche).
    resultCompressed: '{count} compressées',
    resultBytesSaved: '{bytes} gagnés',
    resultNoGain: 'Aucun gain',
    resultPotentialGain: 'Simulation · gain potentiel {bytes} sur {total}',
    resultSkipped: '{count} ignorées',
    resultFailed: '{count} échec(s)',
    resultMoved: '{count} déplacées',
    resultRotated: '{count} pivotées',
    resultRenamed: '{count} renommées',
    resultConflicts: '{count} conflit(s)',
    resultPurged: '{count} purgées',
    resultMissing: '{count} manquant(s)',
    resultFoldersRemoved: '{count} dossier(s) supprimé(s)',
    resultSystemFilesRemoved: '{count} fichier(s) système effacé(s)',
  },
  organize: {
    // Boutons d'entrée (barre d'outils galerie).
    moveWithAudio: 'Déplacer les vidéos avec son…',
    moveSelection: 'Déplacer la sélection…',
    // Modale de déplacement.
    moveTitle: 'Déplacer vers un dossier',
    moveWithAudioIntro:
      'Range toutes les vidéos avec piste audio dans un dossier de la bibliothèque. Les fichiers sont déplacés (pas copiés).',
    moveSelectionIntro:
      'Range les {count} médias sélectionnés dans un dossier de la bibliothèque. Les fichiers sont déplacés (pas copiés).',
    // Destination : arbre de dossiers + carte « Nouveau dossier ici » (comme le téléversement).
    destLabel: 'Dossier de destination',
    destSelected: 'Destination sélectionnée',
    newFolderHere: 'Nouveau dossier ici',
    searchPlaceholder: 'Rechercher un dossier…',
    loadingFolders: 'Chargement des dossiers…',
    foldersError: 'Impossible de charger les dossiers.',
    selectFolder: 'Choisir le dossier {name}',
    folderCount: '{count} fichier(s)',
    expandFolder: 'Déplier {name}',
    collapseFolder: 'Replier {name}',
    searchEmpty: 'Aucun dossier ne correspond à cette recherche',
    newFolderConfirm: 'OK pour créer un dossier dans « {parent} » ?',
    newFolderCreate: 'Créer ici',
    newFolderLabel: 'Nouveau dossier',
    newFolderPreview: 'Sera créé : {path}',
    destPlaceholder: 'ex. with_sound',
    destHint: 'Chemin relatif sous la racine de la bibliothèque (sous-dossiers autorisés avec /).',
    destInvalid: 'Dossier invalide (pas de chemin absolu ni de « .. »).',
    submit: 'Déplacer',
    submitting: 'Enfilement…',
    cancel: 'Annuler',
    close: 'Fermer',
    success: 'Déplacement enfilé. Suivez la progression dans la cloche de notifications.',
    errorWriteDisabled: 'Écriture désactivée sur la bibliothèque (montée en lecture seule).',
    error: 'Le déplacement a échoué. Réessayez.',
  },
  // XPHOTO-32 (lots 4 et 5) : ecritures sur les DOSSIERS eux-memes — creer, renommer,
  // deplacer, supprimer. Distinct d'`organize`, qui deplace des FICHIERS vers un dossier.
  folderOps: {
    newFolder: 'Nouveau dossier',
    menuLabel: 'Actions du dossier {name}',
    actionRename: 'Renommer…',
    actionMove: 'Déplacer vers…',
    actionCreateChild: 'Nouveau sous-dossier…',
    actionDelete: 'Supprimer…',
    createTitle: 'Nouveau dossier',
    renameTitle: 'Renommer le dossier',
    moveTitle: 'Déplacer le dossier',
    deleteTitle: 'Supprimer le dossier',
    createIntro: 'Crée un sous-dossier dans « {folder} ».',
    renameIntro: 'Renomme « {folder} ». Le dossier reste au même endroit.',
    moveIntro: 'Déplace « {folder} » sous un autre dossier. Son nom est conservé.',
    deleteIntro: 'Supprime « {folder} » et tous ses sous-dossiers. Cette action est définitive.',
    nameLabel: 'Nom du dossier',
    namePlaceholder: 'ex. vacances',
    nameHint: 'Un seul nom de dossier, sans « / » ni « \\ ».',
    nameInvalid:
      'Nom invalide (pas de « / », de « \\ », de « .. » ni de nom commençant par un point).',
    preview: 'Chemin résultant : {path}',
    destLabel: 'Dossier de destination',
    createSubmit: 'Créer',
    renameSubmit: 'Renommer',
    moveSubmit: 'Déplacer',
    deleteSubmit: 'Supprimer',
    // Confirmation de SUPPRESSION (lot 5) : ce qui va disparaitre, dit AVANT de le faire.
    deleteScanning: 'Analyse du dossier…',
    deletePreviewError: 'Impossible d’analyser ce dossier. Fermez cette fenêtre et réessayez.',
    deleteSafetyNote:
      'Seuls des répertoires VIDES sont supprimés : aucune photo, aucune vidéo, aucun fichier ne peut être effacé — à la seule exception des fichiers système ci-dessous, et uniquement si vous l’autorisez.',
    deleteBlockedTitle: 'Ce dossier ne peut pas être supprimé :',
    blockerIndexedAssets_one:
      '{count} média est encore indexé dans ce dossier. Videz-le, ou nettoyez l’index dans la console Admin (« Poubelle », « Fichiers absents »).',
    blockerIndexedAssets_other:
      '{count} médias sont encore indexés dans ce dossier. Videz-le, ou nettoyez l’index dans la console Admin (« Poubelle », « Fichiers absents »).',
    blockerOtherFiles:
      'Il contient encore des fichiers. Déplacez-les ou supprimez-les d’abord — ce produit n’efface jamais un fichier à votre place.',
    blockerHiddenDir:
      'Il contient un dossier caché (par exemple « .stfolder » de Syncthing). Ces dossiers sont des marqueurs : ils ne sont jamais supprimés.',
    blockerUnreadable: 'Un de ses sous-dossiers n’a pas pu être lu (droits d’accès).',
    deleteFolderCount_one: '{count} répertoire sera supprimé.',
    deleteFolderCount_other: '{count} répertoires seront supprimés.',
    deleteSystemCount_one: '{count} fichier système empêche seul la suppression.',
    deleteSystemCount_other: '{count} fichiers système empêchent seuls la suppression.',
    deleteSystemNames: 'Fichiers concernés : {names}.',
    deleteSystemOptIn: 'Effacer aussi ces fichiers système',
    // Mot SANS accent : sur un clavier suisse, une majuscule accentuee est malaisee a
    // taper. La comparaison reste tolerante aux accents et a la casse.
    deleteConfirmWord: 'SUPPRIMER',
    deleteConfirmLabel: 'Pour confirmer, recopiez « {word} » :',
    // Ce qui est recale AUTOMATIQUEMENT, dans la meme transaction que le renommage.
    impactIntro: 'Suivront automatiquement :',
    impactAssets_one: '{count} média',
    impactAssets_other: '{count} médias',
    impactShares_one: '{count} partage public',
    impactShares_other: '{count} partages publics',
    impactGrants_one: '{count} accès accordé',
    impactGrants_other: '{count} accès accordés',
    // Ce qui NE suit PAS : le predicat `dossier:` d'un filtre enregistre n'est jamais
    // reecrit (decision produit) — on avertit, l'utilisateur decide.
    savedFiltersWarning_one:
      '{count} filtre enregistré référence ce dossier : son expression ne sera PAS mise à jour et cessera de fonctionner.',
    savedFiltersWarning_other:
      '{count} filtres enregistrés référencent ce dossier : leurs expressions ne seront PAS mises à jour et cesseront de fonctionner.',
    errorWriteDisabled: 'Écriture désactivée sur la bibliothèque (montée en lecture seule).',
    errorConflict: 'Un dossier porte déjà ce nom à cet emplacement. Rien n’a été modifié.',
    errorCrossDevice:
      'La destination est sur un autre volume : ce déplacement n’est pas pris en charge.',
    errorOutOfSync:
      'Le dossier a été renommé sur le disque mais la base n’a pas suivi. Lancez une analyse de la bibliothèque.',
    errorNotEmpty:
      'Le dossier n’est plus vide : son contenu a changé depuis l’analyse. Rien n’a été supprimé — rouvrez cette fenêtre pour voir l’état actuel.',
    errorHasAssets:
      'Des médias sont encore indexés dans ce dossier. Videz-le, ou nettoyez l’index dans la console Admin (« Poubelle », « Fichiers absents »), puis réessayez.',
    errorInvalid: 'Nom ou destination invalide.',
    errorGeneric: 'L’opération a échoué. Réessayez.',
  },
  compress: {
    // Entrée du menu Actions + modale de compression SANS PERTE.
    action: 'Compresser la sélection…',
    title: 'Compresser la sélection',
    intro: 'Réduit la taille des {count} médias sélectionnés sans aucune perte de qualité.',
    losslessNote:
      'Compression SANS PERTE (pixels identiques) — images JPEG et PNG uniquement ; les vidéos sont ignorées. Un fichier n’est remplacé que s’il devient plus petit.',
    keepOriginal: 'Conserver une sauvegarde « _original »',
    dryRun: 'Simuler d’abord (aperçu du gain, sans écrire)',
    confirm: 'Compresser',
    submitting: 'Enfilement…',
    cancel: 'Annuler',
    close: 'Fermer',
    queued: 'Compression enfilée. Suivez la progression dans la cloche de notifications.',
    errorWriteDisabled: 'Écriture désactivée sur la bibliothèque (montée en lecture seule).',
    error: 'La compression a échoué. Réessayez.',
  },
  rotate: {
    // Entrée du menu Actions + modale « Pivoter » (rotation par le tag EXIF Orientation).
    action: 'Pivoter la sélection…',
    title: 'Pivoter la sélection',
    intro: 'Fait pivoter les {count} médias sélectionnés en écrivant leur orientation.',
    exifNote:
      'La rotation écrit SEULEMENT le tag d’orientation EXIF des originaux — les pixels ne sont jamais modifiés (aucune perte de qualité). Les vidéos sont ignorées.',
    cw: 'Pivoter à droite (90°)',
    ccw: 'Pivoter à gauche (90°)',
    flip: 'Retourner (180°)',
    dryRun: 'Simuler d’abord (aperçu, sans écrire)',
    queued: 'Rotation enfilée. Suivez la progression dans la cloche de notifications.',
    cancel: 'Annuler',
    close: 'Fermer',
    errorWriteDisabled: 'Écriture désactivée sur la bibliothèque (montée en lecture seule).',
    error: 'La rotation a échoué. Réessayez.',
  },
  fixExtensions: {
    // Modale « Corriger les extensions » (toute la bibliothèque).
    title: 'Corriger les extensions',
    intro:
      'Renomme les fichiers dont l’extension ne correspond pas à leur type réel (un vrai JPEG nommé « .png », un WebP nommé « .jpg », une vidéo WEBM nommée « .mp4 »…) vers la bonne extension.',
    note: 'Le CONTENU du fichier n’est pas modifié (simple renommage). En cas de collision de nom, un suffixe « (1) » est ajouté — aucun fichier n’est jamais écrasé.',
    scanning: 'Analyse en cours…',
    scanError: 'L’analyse a échoué.',
    retry: 'Réessayer',
    none: 'Aucun fichier mal étiqueté : toutes les extensions correspondent au contenu.',
    count: '{count} fichier(s) à corriger :',
    andMore: '… et {count} autre(s).',
    confirm: 'Corriger {count} fichier(s)',
    submitting: 'Enfilement…',
    cancel: 'Annuler',
    close: 'Fermer',
    queued: 'Correction enfilée. Suivez la progression dans la cloche de notifications.',
    errorWriteDisabled: 'Écriture désactivée sur la bibliothèque (montée en lecture seule).',
    error: 'La correction a échoué. Réessayez.',
  },
  cleanEmptyFolders: {
    // Modale « Nettoyer les dossiers vides » (toute la bibliothèque).
    title: 'Nettoyer les dossiers vides',
    intro:
      'Supprime sur le disque les dossiers qui ne contiennent plus aucun fichier — ceux que laissent derrière eux un rangement, une purge de la corbeille ou un ménage fait à la main. Ces dossiers n’apparaissent nulle part dans xphoto : la liste des dossiers est dérivée des photos.',
    note: 'Aucun fichier n’est jamais effacé : l’outil ne fait que retirer des dossiers vides. Les dossiers cachés (commençant par un point) sont laissés intacts — ce sont souvent des marqueurs d’outils de synchronisation.',
    scanning: 'Analyse en cours…',
    scanError: 'L’analyse a échoué.',
    retry: 'Réessayer',
    none: 'Aucun dossier vide : rien à nettoyer.',
    count: '{count} dossier(s) vide(s) à supprimer :',
    systemCount: '{count} dossier(s) ne contenant que des fichiers système :',
    systemNote:
      'Ces dossiers paraissent vides, mais votre système d’exploitation y a laissé un fichier ({names}). Ils ne sont PAS supprimés sans votre accord.',
    systemOptIn: 'Effacer aussi ces fichiers système, puis supprimer ces dossiers',
    andMore: '… et {count} autre(s).',
    confirm: 'Supprimer {count} dossier(s)',
    submitting: 'Enfilement…',
    cancel: 'Annuler',
    close: 'Fermer',
    queued: 'Nettoyage enfilé. Suivez la progression dans la cloche de notifications.',
    errorWriteDisabled: 'Écriture désactivée sur la bibliothèque (montée en lecture seule).',
    error: 'Le nettoyage a échoué. Réessayez.',
  },
  upload: {
    // Modale de téléversement (XPHOTO-16).
    title: 'Importer des fichiers',
    enlarge: 'Agrandir la fenêtre',
    reduce: 'Réduire la fenêtre',
    intro:
      'Ajoutez des photos ou vidéos à la bibliothèque. Les fichiers sont copiés dans le dossier choisi puis indexés (suivez dans la cloche).',
    heicNote:
      'Les photos HEIC (iPhone) sont converties en WebP à l’import, car les navigateurs n’affichent pas le HEIC : l’original HEIC N’EST PAS conservé. La conversion est irréversible et perd un peu de qualité.',
    dropHint: 'Glissez-déposez des fichiers ici, ou choisissez-les.',
    chooseFiles: 'Choisir des fichiers',
    selectedCount: '{count} fichier(s) sélectionné(s)',
    removeFile: 'Retirer {name}',
    destLabel: 'Dossier de destination',
    destSelected: 'Destination sélectionnée',
    newFolderHere: 'Nouveau dossier ici',
    searchPlaceholder: 'Rechercher un dossier…',
    loadingFolders: 'Chargement des dossiers…',
    foldersError: 'Impossible de charger les dossiers.',
    selectFolder: 'Choisir le dossier {name}',
    folderCount: '{count} fichier(s)',
    expandFolder: 'Déplier {name}',
    collapseFolder: 'Replier {name}',
    searchEmpty: 'Aucun dossier ne correspond à cette recherche',
    newFolderConfirm: 'OK pour créer un dossier dans « {parent} » ?',
    newFolderCreate: 'Créer ici',
    newFolderNameLabel: 'Nom du nouveau dossier',
    newFolderNamePlaceholder: 'ex. 2026',
    newFolderNameHint: 'Saisissez le nom du dossier à créer dans l’emplacement choisi.',
    newFolderPreview: 'Sera créé : {path}',
    destInvalid: 'Dossier invalide (pas de chemin absolu ni de « .. »).',
    submit: 'Importer',
    uploading: 'Import en cours…',
    cancel: 'Annuler',
    close: 'Fermer',
    resultUploaded: '{count} fichier(s) importé(s).',
    resultIndexing: "L'indexation est en cours (suivez-la dans la cloche de notifications).",
    resultSkipped: '{count} fichier(s) ignoré(s) :',
    skipNonMedia: 'type non pris en charge',
    skipBadName: 'nom de fichier invalide',
    skipEmpty: 'fichier vide',
    skipWriteError: "échec de l'écriture",
    skipConvertFailed: 'échec de la conversion',
    skipTooLarge: 'image trop grande (panorama)',
    errorWriteDisabled:
      "L'écriture sur la bibliothèque est désactivée. Un administrateur doit monter /library en écriture et activer LIBRARY_WRITABLE.",
    errorForbidden: "Vous n'avez pas le droit d'écrire dans cette bibliothèque.",
    error: 'Le téléversement a échoué. Réessayez.',
  },
  trash: {
    // Bouton de la barre de sélection (galerie).
    deleteSelection: 'Supprimer la sélection',
    // Confirmation « mise à la corbeille » — insiste sur la RÉVERSIBILITÉ (aucun fichier
    // effacé du disque ; effacement définitif seulement depuis l'administration).
    confirmTitle: 'Mettre à la corbeille (réversible)',
    confirmBody:
      'Mettre {count} fichier(s) à la corbeille ? C’est réversible : les fichiers restent restaurables depuis l’administration et AUCUN fichier n’est effacé du disque. L’effacement définitif reste possible plus tard depuis l’administration.',
    confirmDelete: 'Mettre à la corbeille',
    cancel: 'Annuler',
    deleting: 'Suppression…',
    error: 'La suppression a échoué. Réessayez.',
    errorForbidden: 'Vous n’avez pas les droits pour supprimer ces fichiers.',
  },
  bulkRating: {
    // Bouton de la barre de sélection (galerie).
    rateSelection: 'Noter la sélection',
    // Modale de notation de masse (0–5 étoiles).
    title: 'Noter la sélection',
    intro: 'Appliquer une note aux {count} média(s) sélectionné(s).',
    selected: 'Note sélectionnée : {rating}/5',
    unrated: 'Non noté (aucune étoile)',
    apply: 'Appliquer',
    applying: 'Enregistrement…',
    cancel: 'Annuler',
    error: 'La notation a échoué. Réessayez.',
    errorForbidden: 'Vous n’avez pas les droits pour noter ces fichiers.',
  },
  bulkTag: {
    // Boutons de la barre de sélection (galerie).
    addTag: 'Ajouter un tag',
    tagAiSelection: "Tagger la sélection avec l'IA",
    // Modale d'ajout de tags existants.
    title: 'Ajouter un tag',
    intro: 'Appliquer un ou plusieurs tags existants aux {count} média(s) sélectionné(s).',
    searchLabel: 'Rechercher un tag',
    searchPlaceholder: 'Filtrer les tags…',
    listLabel: 'Tags existants',
    loading: 'Chargement des tags…',
    empty: 'Aucun tag — gérez la taxonomie pour en créer.',
    noMatch: 'Aucun tag ne correspond à la recherche.',
    usageCount: 'utilisé {count}×',
    chosenCount: '{count} tag(s) sélectionné(s)',
    apply: 'Appliquer',
    applying: 'Enregistrement…',
    cancel: 'Annuler',
    error: "L'ajout de tags a échoué. Réessayez.",
    errorForbidden: 'Vous n’avez pas les droits pour taguer ces fichiers.',
    loadError: 'Impossible de charger les tags.',
    // Mode « Retirer » (bascule de la modale de tags).
    modeAdd: 'Ajouter',
    modeRemove: 'Retirer',
    modeGroupLabel: 'Ajouter ou retirer des tags',
    titleRemove: 'Retirer un tag',
    introRemove: 'Retirer un ou plusieurs tags des {count} média(s) sélectionné(s).',
    listLabelRemove: 'Tags présents sur la sélection',
    emptyRemove: 'Aucun tag n’est posé sur la sélection.',
    presentCount: 'sur {count} média(s)',
    removeApply: 'Retirer',
    removing: 'Retrait…',
    removeError: 'Le retrait de tags a échoué. Réessayez.',
    // « Accepter les suggestions IA » (menu Actions d'une sélection).
    acceptAi: 'Accepter les suggestions IA',
    acceptAiTitle: 'Accepter les suggestions IA',
    acceptAiBody:
      'Les tags proposés par l’IA sur les {count} photo(s) sélectionnée(s) passent en « validés ». Les photos restent « À valider (IA) » jusqu’à ce que vous les finalisiez.',
    acceptAiConfirm: 'Accepter',
    acceptAiBusy: 'Acceptation…',
    acceptAiError: "L'acceptation des suggestions a échoué. Réessayez.",
    acceptAiForbidden: 'Vous n’avez pas les droits pour valider ces tags.',
  },
  aiSwitch: {
    label: 'Activer les fonctions IA',
    hintEnabled:
      "Les fonctions IA sont actives : « Tagger la sélection avec l'IA » est proposé et le tagging par le LLM peut être lancé.",
    hintDisabled:
      "Les fonctions IA sont désactivées : « Tagger la sélection avec l'IA » est masqué pour tous les rôles et aucun tagging IA ne peut être lancé. Le superadmin peut les réactiver ici.",
    forbidden: 'Accès refusé : réservé au superadmin.',
    error: 'Impossible de mettre à jour le réglage. Réessayez.',
  },
  llm: {
    tab: 'Configuration LLM',
    title: 'Configuration LLM',
    intro:
      'Configurez le fournisseur de vision utilisé pour proposer des tags. Testez la connexion, découvrez les modèles disponibles, puis enregistrez et activez le fournisseur.',
    providerLabel: 'Fournisseur',
    providerLmstudio: 'LM Studio (local)',
    providerOllama: 'Ollama (local)',
    providerClaude: 'Claude (Anthropic)',
    providerGemini: 'Gemini (Google)',
    providerGrok: 'Grok (xAI)',
    activeSuffix: 'Actif',
    baseUrlLabel: 'URL de base',
    baseUrlHint:
      'Requise pour LM Studio (serveur local). Un défaut sensé est proposé pour les fournisseurs cloud.',
    modelLabel: 'Modèle',
    modelPlaceholder: 'Nom du modèle',
    modelHint: 'Nom du modèle à utiliser pour ce fournisseur.',
    modelHintLmstudio: 'Requis pour ce fournisseur (nom du modèle chargé localement).',
    modelDiscoveredLabel: 'Modèles découverts',
    modelClickToChoose: 'cliquer pour choisir',
    modelDiscoveredEmpty: 'Aucun modèle exposé par ce fournisseur.',
    loadError:
      'Impossible de charger les fournisseurs enregistrés. Vous pouvez tout de même en configurer un.',
    actionModeLabel: "Mode d'action IA",
    actionModeAuto: 'Automatique (recommandé)',
    actionModeNative: 'Outils natifs',
    actionModeJson: 'JSON dans le texte',
    actionModeHint:
      "Comment l'IA déclenche ses actions. « Automatique » tente les outils natifs puis retombe sur le JSON en cas d'échec. « Outils natifs » exige le function-calling (modèle capable). « JSON dans le texte » convient aux modèles sans function-calling (ex. Gemma) et évite un appel voué à l'échec.",
    captureFloorLabel: 'Plancher de capture (%)',
    captureFloorHint:
      'Le modèle note tous les tags de la taxonomie ; on ne conserve que les propositions de score ≥ ce plancher (les autres sont écartées). Défaut 10 %. Baissez-le pour capter davantage de tags incertains.',
    promptTemplateLabel: "Prompt d'indexation (anglais)",
    promptTemplateHint:
      'Modèle du prompt envoyé au LLM (en anglais). Utilisez {{tags}} pour la liste des tags et {{maxTags}} pour le nombre max. Laissez vide pour le prompt par défaut. Le prompt réellement envoyé apparaît dans le Journal des appels IA.',
    promptTemplateReset: 'Réinitialiser au prompt par défaut',
    apiKeyDetected: 'Clé API détectée',
    apiKeyMissing:
      "Clé API manquante : définissez la variable d'environnement indiquée ci-dessous.",
    apiKeyNotRequired: 'Aucune clé requise (exécution locale).',
    apiKeyUnknown: 'Testez la connexion pour vérifier la présence de la clé.',
    testConnection: 'Tester la connexion',
    testConnectionOk: 'Connexion OK',
    testing: 'Test en cours…',
    testReachable: 'Fournisseur joignable · {count} modèle(s) découvert(s).',
    testUnreachable: 'Fournisseur injoignable.',
    visionNoModel: 'Renseignez un modèle pour tester la génération et la vision.',
    visionModelOk: 'Le modèle répond (texte).',
    visionModelFail: 'Le modèle ne répond pas.',
    visionImageOk: 'Le modèle accepte les images (vision).',
    visionImageFail: 'Le modèle refuse les images (ce n’est pas un modèle de vision).',
    saveAndActivate: 'Enregistrer et activer',
    saving: 'Enregistrement…',
    saved: 'Fournisseur enregistré et activé.',
    unsavedChanges: 'Modifications non enregistrées',
    errorModelRequired: 'Renseignez un modèle avant d’enregistrer.',
    errorBaseUrlRequired: "Renseignez l'URL de base avant d'enregistrer.",
    errorForbidden: 'Accès refusé : réservé au propriétaire de la bibliothèque ou au superadmin.',
    errorGeneric: 'Une erreur est survenue. Réessayez.',
    hintsTitle: 'Indications fournisseur',
    hintProvider: 'Fournisseur',
    hintEnvVar: "Variable d'environnement de la clé",
    hintNoEnvVar: 'Aucune (exécution locale)',
    hintDoc: 'Console / documentation',
    hintDocLink: 'Ouvrir',
    noteLmstudio:
      "Serveur local compatible OpenAI : pas de clé API. Démarrez le serveur puis renseignez l'URL de base (défaut http://localhost:1234/v1) et le modèle.",
    noteOllama:
      "Serveur local : pas de clé API. Démarrez Ollama, installez un modèle de vision, puis renseignez l'URL de base (défaut http://localhost:11434) et le modèle.",
    noteClaude:
      "Fournisseur cloud d'Anthropic. Créez une clé dans la console, exposez-la via la variable d'environnement XPHOTO_AI_KEY_ANTHROPIC, puis choisissez un modèle de vision.",
    noteGemini:
      "Fournisseur cloud de Google. Créez une clé dans AI Studio, exposez-la via la variable d'environnement XPHOTO_AI_KEY_GEMINI, puis choisissez un modèle de vision.",
    noteGrok:
      "Fournisseur cloud de xAI (compatible OpenAI). Créez une clé dans la console, exposez-la via la variable d'environnement XPHOTO_AI_KEY_XAI, puis choisissez un modèle.",
  },
  taxonomy: {
    title: 'Taxonomie',
    close: 'Fermer',
    cancel: 'Annuler',
    intro:
      'Gérez le vocabulaire de tags de votre bibliothèque : un seul libellé par tag, sans catégorie ni hiérarchie. Créez, renommez, supprimez ou fusionnez des tags.',
    createLabel: 'Nouveau tag',
    createPlaceholder: 'Nom du nouveau tag…',
    createAction: 'Créer',
    searchLabel: 'Rechercher un tag',
    searchPlaceholder: 'Filtrer par nom…',
    sortLabel: 'Trier par',
    sortName: 'Nom',
    sortUsage: 'Usage',
    usageCount: 'utilisé sur {count} photos',
    usageNone: 'utilisé sur aucune photo',
    candidate: 'Candidat',
    hideCandidates: 'Masquer les candidats ({count})',
    loading: 'Chargement du vocabulaire…',
    loadError: 'Impossible de charger le vocabulaire.',
    empty: 'Aucun tag pour le moment. Créez-en un ci-dessus.',
    noMatch: 'Aucun tag ne correspond à la recherche.',
    renameAction: 'Renommer',
    renameAria: 'Renommer le tag {label}',
    renameLabel: 'Nouveau nom du tag',
    renameSave: 'Enregistrer',
    renameCancel: 'Annuler',
    descriptionAria: 'Description du tag {label} (facultatif, guide l’IA)',
    descriptionPlaceholder: 'ex. « arbres, fleurs, montagnes… »',
    descriptionSaving: 'Enregistrement…',
    deleteAction: 'Supprimer',
    deleteAria: 'Supprimer le tag {label}',
    deleteTitle: 'Supprimer ce tag ?',
    deleteBody: 'Le tag « {label} » sera définitivement supprimé du vocabulaire.',
    deleteUsedWarning:
      'Ce tag est utilisé sur {count} photos : il en sera retiré. Cette action est irréversible.',
    deleteConfirm: 'Supprimer',
    deleting: 'Suppression…',
    selectForMerge: 'Sélectionner le tag {label} pour la fusion',
    mergeSelected: '{count} tags sélectionnés',
    mergeTargetLabel: 'Conserver',
    mergeTargetPlaceholder: 'Choisir le tag à conserver…',
    mergeAction: 'Fusionner',
    mergeClear: 'Effacer la sélection',
    mergeHint: 'Sélectionnez au moins deux tags pour les fusionner.',
    mergeTitle: 'Fusionner les tags ?',
    mergeBody:
      'Les {count} tags ({sources}) seront fusionnés dans « {target} » : leurs photos y seront réaffectées, puis ces tags seront supprimés.',
    mergeNote:
      'Le tag conservé porte alors toutes les photos concernées. Cette action est irréversible.',
    mergeConfirm: 'Fusionner',
    merging: 'Fusion…',
    errorDuplicate: 'Ce tag existe déjà.',
    errorGeneric: "L'opération a échoué. Réessayez.",
  },
  tagging: {
    title: 'Réviser / valider les tags',
    panelTitle: 'Tags de la photo',
    listLabel: 'Tags de la taxonomie',
    validateAndNext: 'Valider et suivant',
    validateAndFinish: 'Valider et terminer',
    validating: 'Validation…',
    confidence: 'IA · {pct} %',
    readonly: 'Accès en lecture seule : impossible de modifier les tags.',
    error: 'La validation a échoué. Réessayez.',
    loading: 'Chargement des tags…',
    errorLoad: 'Impossible de charger les tags.',
    retry: 'Réessayer',
    empty: 'Aucun tag dans la taxonomie. Créez-en dans « Gérer les tags ».',
  },
  aiTrace: {
    title: 'Journal des appels IA',
    tab: 'Journal des appels IA',
    intro:
      "Chaque appel du tagging IA au modèle de vision est tracé ici : le prompt envoyé (vos tags et leurs descriptions, avec l'interdiction d'en créer de nouveaux) et la réponse brute du modèle. Utile pour vérifier ce qui part et ce qui revient.",
    total: '{count} appels enregistrés',
    model: 'Modèle',
    promptLabel: 'Prompt envoyé',
    responseLabel: 'Réponse du modèle',
    noResponse: '(aucune réponse)',
    reachable: 'Répondu',
    unreachable: 'Injoignable',
    summary: '{linked} tags liés · {newLabels} hors taxonomie ignorés',
    errorLabel: 'Erreur',
    prevPage: 'Précédent',
    nextPage: 'Suivant',
    pageStatus: '{from}–{to} sur {total}',
    loading: 'Chargement du journal…',
    errorLoad: 'Impossible de charger le journal.',
    retry: 'Réessayer',
    empty: 'Aucun appel enregistré',
    emptyHint:
      "Sélectionnez des photos, puis « Actions… » → « Tagger la sélection avec l'IA » pour générer des appels.",
  },
  advancedSearch: {
    open: 'Recherche',
    title: 'Recherche',
    intro: 'Recherchez par nom de fichier, note et tags. Les critères se cumulent.',
    filenameSection: 'Nom de fichier',
    filenamePlaceholder: 'Rechercher par nom de fichier…',
    ratingSection: 'Note minimale',
    ratingAny: 'Toutes les notes',
    ratingAtLeast: 'Au moins {rating}/5',
    tagsSection: 'Tags',
    tagModeLabel: 'Correspondance des tags',
    tagModeAny: 'Au moins un',
    tagModeAll: 'Tous',
    apply: 'Appliquer',
    reset: 'Réinitialiser',
    cancel: 'Annuler',
    chipsLabel: 'Filtres de recherche actifs',
    chipFilename: 'Nom : {q}',
    chipRating: 'Note ≥ {rating}★',
    chipTagsAny: 'Tags (au moins un) : {count}',
    chipTagsAll: 'Tags (tous) : {count}',
    removeFilename: 'Retirer le filtre de nom',
    removeRating: 'Retirer le filtre de note',
    removeTags: 'Retirer le filtre de tags',
  },
  savedFilters: {
    open: 'Mes filtres',
    none: 'Aucun filtre enregistré',
    loading: 'Chargement…',
    loadError: 'Impossible de charger vos filtres.',
    applyLabel: 'Appliquer le filtre {name}',
    saveCurrent: 'Enregistrer le filtre actuel…',
    editExpr: 'Modifier l’expression…',
    manage: 'Gérer mes filtres…',
    saveTitle: 'Enregistrer le filtre',
    nameLabel: 'Nom du filtre',
    namePlaceholder: 'Ex. Vacances 4★ et plus',
    exprPreview: 'Expression enregistrée',
    iconLabel: 'Icône',
    iconNone: 'Aucune icône',
    save: 'Enregistrer',
    saving: 'Enregistrement…',
    cancel: 'Annuler',
    errorNameTaken: 'Ce nom est déjà pris.',
    errorStale: 'Ce filtre a été modifié ailleurs entre-temps. Rechargez avant d’enregistrer.',
    errorEmptyExpr: 'Composez d’abord un filtre à enregistrer.',
    errorSave: 'Impossible d’enregistrer le filtre. Réessayez.',
    manageTitle: 'Gérer mes filtres',
    manageEmpty: 'Vous n’avez aucun filtre enregistré.',
    renameLabel: 'Renommer {name}',
    changeIcon: 'Changer l’icône de {name}',
    delete: 'Supprimer',
    deleteAria: 'Supprimer le filtre {name}',
    moveUp: 'Monter {name}',
    moveDown: 'Descendre {name}',
    deleteConfirmTitle: 'Supprimer le filtre',
    deleteConfirmBody: 'Supprimer le filtre « {name} » ? Cette action est définitive.',
    deleteConfirm: 'Supprimer',
    deleting: 'Suppression…',
    errorUpdate: 'Impossible de mettre à jour le filtre. Réessayez.',
    errorReorder: 'Impossible de réordonner les filtres. Réessayez.',
    errorDelete: 'Impossible de supprimer le filtre. Réessayez.',
    done: 'Fermer',
    iconName: {
      funnel: 'Entonnoir',
      star: 'Étoile',
      camera: 'Appareil photo',
      videoCamera: 'Caméra',
      sparkles: 'Étincelles',
      tag: 'Étiquette',
      sun: 'Soleil',
      heart: 'Cœur',
      bookmark: 'Signet',
      photo: 'Photo',
    },
  },
  filterExpr: {
    editTitle: 'Expression avancée',
    intro: 'Composez un filtre avec ET, OU, SAUF et des parenthèses.',
    fieldLabel: 'Expression de filtre',
    placeholder: 'Ex. note>=4 OU (tag:plage ET tag:été)',
    echoLabel: 'Lecture :',
    empty: 'Saisissez une expression pour la prévisualiser.',
    paletteLabel: 'Insérer un tag',
    connector: 'Enchaîner avec',
    connectorOr: 'OU',
    connectorAnd: 'ET',
    connectorExcept: 'SAUF',
    paletteSearchLabel: 'Rechercher un tag',
    paletteSearchPlaceholder: 'Rechercher un tag…',
    legendTitle: 'Vocabulaire',
    legend: 'type:  note>=  tag:  dossier:  nom:  statut:  ·  ET  OU  SAUF  ( )',
    reset: 'Réinitialiser',
    saveAs: 'Enregistrer sous…',
    apply: 'Appliquer',
    cancel: 'Annuler',
    and: 'et',
    or: 'ou',
    except: 'sauf {x}',
    leaf: {
      tag: 'taguées « {label} »',
      type: 'de type {type}',
      ratingGte: 'notées {value}★ et plus',
      ratingLte: 'notées {value}★ et moins',
      ratingEq: 'notées exactement {value}★',
      folder: 'dans le dossier « {folder} »',
      folderRoot: 'à la racine',
      filename: 'au nom contenant « {name} »',
      status: 'au statut {status}',
    },
    typeName: {
      image: 'image',
      animatedImage: 'image animée',
      video: 'vidéo',
      videoWithSound: 'vidéo avec son',
    },
    statusName: {
      tagged: 'taguée',
      untagged: 'non taguée',
      aiPending: 'à valider (IA)',
    },
    error: {
      empty: 'Saisissez une expression.',
      unexpectedToken: 'Symbole inattendu à la position {position}.',
      unexpectedEof: 'Expression incomplète : un critère est attendu.',
      unclosedParen: 'Parenthèse ouvrante non fermée.',
      unclosedQuote: 'Guillemet non fermé.',
      invalidRating: 'Note invalide (entier de 0 à 5).',
      unknownType: 'Type inconnu (image, animée, vidéo, vidéo-son).',
      unknownStatus: 'Statut inconnu (taguée, non-taguée, à-valider).',
      unknownField: 'Préfixe de champ inconnu (type, note, tag, dossier, nom, statut).',
      emptyValue: 'Valeur manquante après le préfixe.',
    },
  },
  exprChips: {
    label: 'Filtre avancé actif',
    advanced: 'Expression avancée',
    remove: 'Retirer le filtre avancé',
  },
  a11y: {
    skipToContent: 'Aller au contenu principal',
    closeMenu: 'Fermer le menu',
  },
} as const;

/**
 * Forme canonique d'un catalogue, dérivée du FR mais avec les feuilles élargies
 * à `string` : DE/EN doivent avoir **les mêmes clés** sans être contraints aux
 * mêmes valeurs littérales que le FR. La parité des clés est ainsi garantie par
 * le type ; le test `i18n.test.ts` la re-vérifie à l'exécution.
 */
type WidenLeaves<T> = {
  [K in keyof T]: T[K] extends string ? string : WidenLeaves<T[K]>;
};

export type Messages = WidenLeaves<typeof fr>;
